import React from "react";
import type { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import {
  IsoDayOfWeek,
  MaintenanceWindowFrequency,
  MonthlyMaintenanceWindowWeekOfMonth,
  type MaintenanceWindowDto,
} from "client/api-v2";
import type { CloudSubscriptionOutageWindowFrequency } from "client/api/CloudSubscriptionApi";
import { updateOutageWindow } from "client/api/CloudSubscriptionApi";
import type { MaintenanceWindowDto as LegacyMaintenanceWindowDto } from "client/api/CloudSubscriptionApi";
import { ChangeOutageWindowDialog } from "components/cloud-subscriptions/ChangeOutageWindowDialog";

type ChangeMaintenanceWindowDialogProps = {
  cloudSubscriptionId: string;
  open: boolean;
  currentMaintenanceWindow: MaintenanceWindowDto;
  handleClose: () => void;
  onSaved: () => void;
};

type ValidWeekDay = "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";

// Converts the v2 MaintenanceWindowDto to the legacy shape that ChangeOutageWindowDialog expects.
// startTime/endTime arrive as DateTime (deserialized by the axios interceptor) despite being typed
// as string in the generated v2 types; getOrParseDateTime handles both cases.
function toLegacyMaintenanceWindow(dto: MaintenanceWindowDto): LegacyMaintenanceWindowDto {
  const base = {
    startTime: getOrParseDateTime(dto.startTime),
    endTime: getOrParseDateTime(dto.endTime),
    timeZone: dto.timeZone,
  };

  if (dto.frequency === MaintenanceWindowFrequency.Daily) {
    return { ...base, frequency: MaintenanceWindowFrequency.Daily };
  }

  if (dto.frequency === MaintenanceWindowFrequency.Weekly) {
    const day = dto.dayOfWeek;
    const validDay: ValidWeekDay =
      day === IsoDayOfWeek.Sunday ||
      day === IsoDayOfWeek.Monday ||
      day === IsoDayOfWeek.Tuesday ||
      day === IsoDayOfWeek.Wednesday ||
      day === IsoDayOfWeek.Thursday ||
      day === IsoDayOfWeek.Friday
        ? day
        : IsoDayOfWeek.Monday;
    return { ...base, frequency: MaintenanceWindowFrequency.Weekly, dayOfWeek: validDay };
  }

  // Monthly
  const week = dto.weekOfMonth;
  const validWeek: "First" | "Second" | "Third" =
    week === MonthlyMaintenanceWindowWeekOfMonth.First ||
    week === MonthlyMaintenanceWindowWeekOfMonth.Second ||
    week === MonthlyMaintenanceWindowWeekOfMonth.Third
      ? week
      : MonthlyMaintenanceWindowWeekOfMonth.First;
  return {
    ...base,
    frequency: MaintenanceWindowFrequency.Monthly,
    weekOfMonth: validWeek,
    dayOfWeek: IsoDayOfWeek.Monday,
  };
}

export function ChangeMaintenanceWindowDialog({
  cloudSubscriptionId,
  open,
  currentMaintenanceWindow,
  handleClose,
  onSaved,
}: ChangeMaintenanceWindowDialogProps) {
  const handleSubmit = async (
    outageWindowFrequency: CloudSubscriptionOutageWindowFrequency,
    outageWindowTimeZone: string,
    outageWindowStart: DateTime,
    outageWindowEnd: DateTime
  ) => {
    await updateOutageWindow(
      cloudSubscriptionId,
      outageWindowFrequency,
      outageWindowTimeZone,
      outageWindowStart,
      outageWindowEnd
    );
    handleClose();
    onSaved();
  };

  return (
    <ChangeOutageWindowDialog
      cloudSubscriptionId={cloudSubscriptionId}
      open={open}
      handleClose={handleClose}
      currentMaintenanceWindow={toLegacyMaintenanceWindow(currentMaintenanceWindow)}
      submitChange={handleSubmit}
    />
  );
}
